import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Clock, AlertCircle, Copy, Users, Search, Filter, X, ExternalLink, Mail, Phone, IndianRupee, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/workshops`;
const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/user-auth`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const statusMeta = {
  pending_verification: { label: 'Pending', color: 'bg-amber-500/15 text-amber-300 border-amber-500/40', Icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', Icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-rose-500/15 text-rose-300 border-rose-500/40', Icon: XCircle },
};

export default function AdminWorkshopsPage() {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // enrollment for detail modal
  const [previewImg, setPreviewImg] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [joinLink, setJoinLink] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('practiq_portal_token') : '';

  // ---- Auth gate: verify founder ----
  useEffect(() => {
    if (!token) { navigate('/portal/login?redirect=/admin/workshops'); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${AUTH_API}/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error('unauth');
        const me = await r.json();
        if (!me.is_founder) throw new Error('forbidden');
        if (!cancelled) setAuthChecking(false);
      } catch {
        toast.error('Admin access required');
        navigate('/portal');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  // ---- Fetch enrollments ----
  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/enrollments`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setEnrollments(data.enrollments || []);
    } catch {
      toast.error('Could not load enrollments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authChecking) return;
    load();
  }, [authChecking]);

  const filtered = useMemo(() => {
    let rows = enrollments;
    if (filter !== 'all') rows = rows.filter((e) => e.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((e) =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q) ||
        (e.phone || '').includes(q) ||
        (e.utr_number || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [enrollments, filter, search]);

  const stats = useMemo(() => ({
    total: enrollments.length,
    pending: enrollments.filter((e) => e.status === 'pending_verification').length,
    confirmed: enrollments.filter((e) => e.status === 'confirmed').length,
    rejected: enrollments.filter((e) => e.status === 'rejected').length,
    revenue: enrollments
      .filter((e) => e.status === 'confirmed')
      .reduce((s, e) => s + (e.amount || 0), 0),
  }), [enrollments]);

  const confirm = async (enrollment, action) => {
    setConfirming(true);
    try {
      const body = { enrollment_id: enrollment.enrollment_id, action };
      if (action === 'confirm' && joinLink.trim()) body.join_link = joinLink.trim();
      const r = await fetch(`${API}/admin/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      toast.success(action === 'confirm' ? 'Enrollment confirmed ✅' : 'Enrollment rejected');
      setSelected(null);
      setJoinLink('');
      await load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setConfirming(false);
    }
  };

  const copyText = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text || '');
    toast.success(label);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white" data-testid="admin-workshops-page">
      {/* Topbar */}
      <header className="border-b border-white/10 bg-[#0F1626]/60 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/portal')} className="text-xs text-gray-400 hover:text-white">
            ← Back to Portal
          </button>
          <div className="w-px h-5 bg-white/10" />
          <h1 className="text-lg font-semibold">Admin · Workshop Enrollments</h1>
        </div>
        <Button onClick={load} size="sm" variant="outline" className="border-white/10 text-white bg-transparent hover:bg-white/5" data-testid="admin-refresh-btn">
          Refresh
        </Button>
      </header>

      <main className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total" value={stats.total} tone="text-white" />
          <StatCard label="Pending" value={stats.pending} tone="text-amber-400" />
          <StatCard label="Confirmed" value={stats.confirmed} tone="text-emerald-400" />
          <StatCard label="Rejected" value={stats.rejected} tone="text-rose-400" />
          <StatCard label="Revenue (₹)" value={stats.revenue.toLocaleString('en-IN')} tone="text-teal-400" />
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search by name, email, phone, UTR…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
            {[
              { k: 'all', label: `All (${stats.total})` },
              { k: 'pending_verification', label: `Pending (${stats.pending})` },
              { k: 'confirmed', label: `Confirmed (${stats.confirmed})` },
              { k: 'rejected', label: `Rejected (${stats.rejected})` },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  filter === f.k
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`admin-filter-${f.k}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-16 text-center">
            <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No enrollments match this filter.</p>
          </div>
        ) : (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Enrolled</th>
                    <th className="text-left px-4 py-3 font-medium">Student</th>
                    <th className="text-left px-4 py-3 font-medium">Contact</th>
                    <th className="text-left px-4 py-3 font-medium">Payment Proof</th>
                    <th className="text-left px-4 py-3 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filtered.map((e) => {
                    const s = statusMeta[e.status] || statusMeta.pending_verification;
                    return (
                      <tr key={e.enrollment_id} className="hover:bg-white/[0.03]" data-testid={`enrollment-row-${e.enrollment_id}`}>
                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs">{formatDate(e.created_at)}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-white">{e.name || '—'}</p>
                          <p className="text-[11px] text-gray-500 truncate max-w-[180px]">{e.workshop_title}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          <p className="text-xs flex items-center gap-1"><Mail className="w-3 h-3 text-gray-500" /> {e.email}</p>
                          <p className="text-xs flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3 text-gray-500" /> {e.phone}</p>
                        </td>
                        <td className="px-4 py-3">
                          {e.utr_number ? (
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-xs bg-white/5 px-2 py-1 rounded">{e.utr_number}</span>
                              <button onClick={() => copyText(e.utr_number, 'UTR copied')} className="text-gray-400 hover:text-teal-400">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          ) : e.screenshot_data_url ? (
                            <button
                              onClick={() => setPreviewImg(e.screenshot_data_url)}
                              className="text-xs text-teal-400 hover:underline flex items-center gap-1"
                              data-testid={`view-screenshot-${e.enrollment_id}`}
                            >
                              View screenshot <ExternalLink className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white font-semibold whitespace-nowrap">₹{e.amount}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${s.color} text-[11px]`}>
                            <s.Icon className="w-3 h-3 mr-1" />
                            {s.label}
                          </Badge>
                          {e.join_link && (
                            <p className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                              <Video className="w-3 h-3" /> Zoom sent
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelected(e); setJoinLink(e.join_link || ''); }}
                            className="border-white/10 text-white bg-transparent hover:bg-white/5 h-7 text-xs"
                            data-testid={`review-${e.enrollment_id}`}
                          >
                            Review
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Detail / Confirm modal */}
      {selected && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={confirming ? undefined : () => setSelected(null)} />
          <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Review Enrollment</p>
                <h3 className="text-lg font-semibold">{selected.name}</h3>
              </div>
              <button onClick={() => setSelected(null)} disabled={confirming} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Email" value={selected.email} icon={Mail} />
                <InfoRow label="Phone" value={selected.phone} icon={Phone} />
                <InfoRow label="Workshop" value={selected.workshop_title} />
                <InfoRow label="Enrollment ID" value={selected.enrollment_id} mono />
                <InfoRow label="Amount" value={`₹${selected.amount}`} icon={IndianRupee} />
                <InfoRow label="Submitted" value={formatDate(selected.created_at)} />
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Payment Proof</p>
                {selected.utr_number ? (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-white/5 px-3 py-1.5 rounded">{selected.utr_number}</span>
                    <Button size="sm" variant="outline" onClick={() => copyText(selected.utr_number, 'UTR copied')} className="border-white/10 text-white bg-transparent hover:bg-white/5 h-8 text-xs">
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                ) : selected.screenshot_data_url ? (
                  <button onClick={() => setPreviewImg(selected.screenshot_data_url)}>
                    <img src={selected.screenshot_data_url} alt="Payment proof" className="max-h-64 rounded-lg border border-white/10" />
                    <p className="text-[11px] text-teal-400 mt-2">Click to enlarge</p>
                  </button>
                ) : (
                  <p className="text-xs text-gray-500">No proof attached</p>
                )}
              </div>

              {selected.status === 'pending_verification' && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Zoom Join Link <span className="text-gray-600 normal-case">(optional — will be shown to user)</span></label>
                  <Input
                    placeholder="https://zoom.us/j/…"
                    value={joinLink}
                    onChange={(ev) => setJoinLink(ev.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="admin-join-link-input"
                  />
                </div>
              )}

              {selected.join_link && selected.status === 'confirmed' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <p className="text-xs text-emerald-300 uppercase tracking-wider mb-1">Zoom link on record</p>
                  <a href={selected.join_link} target="_blank" rel="noreferrer" className="text-sm text-emerald-200 hover:underline break-all">{selected.join_link}</a>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 px-6 py-4 flex items-center justify-end gap-2">
              {selected.status === 'pending_verification' ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => confirm(selected, 'reject')}
                    disabled={confirming}
                    className="border-rose-500/40 text-rose-300 bg-transparent hover:bg-rose-500/10"
                    data-testid="admin-reject-btn"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => confirm(selected, 'confirm')}
                    disabled={confirming}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white"
                    data-testid="admin-confirm-btn"
                  >
                    {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm & Save Zoom Link'}
                  </Button>
                </>
              ) : (
                <Button onClick={() => setSelected(null)} className="bg-white/5 hover:bg-white/10 text-white border border-white/10">
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Screenshot preview lightbox */}
      {previewImg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setPreviewImg(null)}>
          <img src={previewImg} alt="Payment proof" className="max-h-[90vh] max-w-full rounded-lg border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setPreviewImg(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, tone }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">{label}</p>
    <p className={`text-2xl font-bold ${tone}`}>{value}</p>
  </div>
);

const InfoRow = ({ label, value, mono = false, icon: Icon }) => (
  <div>
    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </p>
    <p className={`text-sm text-white ${mono ? 'font-mono text-xs' : ''} truncate`}>{value || '—'}</p>
  </div>
);
